import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  Injector,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  SimpleChanges,
} from '@angular/core';
import { combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../../forms/fields/select/ziro-select.component';
import { first, map } from 'rxjs/operators';
import { CurrentClusterContext } from '../../../../contexts/current-cluster.context';
import { SearchDirectoryNumberResource } from '../../../../../self-serve/resources/search/search-directory-number.resource';
import { ZiroFormAbstractDirective } from '../../../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../../forms/ziro-forms-models';
import {
  Button,
  ClusterResult,
  DirectoryNumberRef,
  DirectoryNumberResult,
  EndUser,
  EndUserRef,
  EndUserResult,
  Global360View,
  Phone,
  ServiceProvisioningLevel,
  SmtpAddress,
  UccxAgentResult,
  Voicemail,
  VoicemailAlternateExtension,
  VoicemailRef,
} from '../../../../models/generated/ziroModels';
import { HtmlInputType, ZiroTextConfig } from '../../../../../forms/fields/text/ziro-text.component';
import { LineButtonPeripheralSummary } from '../../../layout/phone-buttons-standard.component';
import { PhoneButtonsService } from '../../../shared/phone-buttons.service';
import { ZiroFormStateService } from '../../../../../forms/ziro-form-state.service';
import { TelephoneNumberFilter } from '../../../../filters/telephone-number.filter';
import { ZiroModalService } from '../../../../services/ziro-modal.service';
import { ToastService } from '../../../../services/toast.service';
import { ToastTypes } from '../../../../services/abstract/toast.service.abstract';
import { ZiroIcons } from '../../../../models/ziro-icons.enum';
import { ZiroDirectoryNumberDetailedModalComponent } from '../../../../../helpdesk/directory-number-detailed-modal/directory-number-detailed-modal.component';
import { UserDetailUiContext } from '../../../../../helpdesk/shared/contexts/user-detail-ui.context';
import { VoicemailDetailedModalComponent } from '../../../../../helpdesk/voicemail-detailed-modal/voicemail-detailed-modal.component';
import { EndUserResource } from '../../../../resources/end-user.resource';
import { Global360ViewContext } from '../../../../contexts/global-360-view.context';
import { cloneDeep, isEqual } from 'lodash';
import { ButtonStyles } from '../../../../../button/button.component';
import { UccxAgentResource } from '../../../../../helpdesk/shared/resources/uccx-agent.resource';
import { PhoneType } from '../../../../models/service-type';
import { DeviceService } from '../../../device.service';
import { ActivatedRoute } from '@angular/router';
import { PhoneUiContext } from '../../../contexts/phone-ui.context';
import { ExtensionMobilityDnD } from '../../../phone-buttons.component';
import { VoicemailService, VoicemailServiceParams } from '../../../../services/voicemail.service';
import { CiscoTileConfigContext } from '../../../../../helpdesk/shared/contexts/cisco-tile-config.context';
import { SiteContext } from '../../../../../helpdesk/shared/contexts/site.context';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ZiroFormsModule } from '../../../../../forms/ziro-forms.module';

interface PhoneButtonsLineExtensionSearchFormData {
  extensionSearch: ZiroSelectOption;
  voicemail: string;
  dialPlanGroupId: number;
}

export interface PhoneButtonsLineExtensionSearchEntity {
  dn: DirectoryNumberRef;
  peripherals: LineButtonPeripheralSummary;
}

@Component({
  selector: 'ziro-phone-buttons-line-extension-search',
  templateUrl: './phone-buttons-line-extension-search.component.html',
  providers: [VoicemailService],
  imports: [ZiroFormsModule, NgClass, TranslateModule],
})
export class PhoneButtonsLineExtensionSearchComponent
  extends ZiroFormAbstractDirective<PhoneButtonsLineExtensionSearchEntity, PhoneButtonsLineExtensionSearchFormData>
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  private currentClusterContext = inject(CurrentClusterContext);
  private searchDirectoryNumberResource = inject(SearchDirectoryNumberResource);
  private telephoneNumberFilter = inject(TelephoneNumberFilter);
  private phoneButtonsService = inject(PhoneButtonsService);
  protected _ziroFormStateService: ZiroFormStateService;
  private ziroModalService = inject(ZiroModalService);
  private toastService = inject(ToastService);
  private userDetailUiContext = inject(UserDetailUiContext);
  private endUserResource = inject(EndUserResource);
  private global360ViewContext = inject(Global360ViewContext);
  private siteContext = inject(SiteContext);
  private injector = inject(Injector);
  private route = inject(ActivatedRoute);
  private _uccxAgentResource = inject(UccxAgentResource);
  private voicemailService = inject(VoicemailService);
  private tileConfigContext = inject(CiscoTileConfigContext);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  readonly isPending = input(false);
  readonly phone = input<Phone | ExtensionMobilityDnD>();
  readonly buttonIndex = input<number>();
  readonly dnRef = input<DirectoryNumberRef>();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() lineButtonSummary: LineButtonPeripheralSummary;
  readonly global360View = input<Global360View>();
  readonly isSingleButton = input<boolean>();
  readonly directoryNumberCreated = output<DirectoryNumberRef>();
  readonly voicemailUpdated = output<VoicemailRef>();
  readonly reconsiderLineFeatureSettings = output<boolean>();

  isOneClickEnabled = true;
  isSelectCleared = false;
  siteId: number;
  voicemailRef: VoicemailRef;
  isVoicemailAdding: boolean;
  isUpdatingSmtpOrHomeCluster = false;
  endUser: EndUser;
  endUserResult: EndUserResult;
  username: string;
  hideHelpText = false;
  isLoading = true;
  modalData: any = null;
  unityServerId: number;
  phoneTypes = PhoneType;
  buttonStyle = ButtonStyles;
  formConfig: ZiroFormConfig;
  updateVmExtension = false;
  phoneType: PhoneType;
  ziroIcons = ZiroIcons;

  private _otherButtons: Button[];
  private _subscription = new Subscription();
  private _currentCluster: ClusterResult;

  ngOnInit(): void {
    this.voicemailRef = this.lineButtonSummary?.voicemail;
    this.phoneType = DeviceService.getPhoneTypeFromRoute(this.route.snapshot);
    this._initForm();
    if (this.phoneType === PhoneType.EXTENSION_MOBILITY) {
      this.endUserResult = this.userDetailUiContext.getCurrentEnduser();
      this.username = this.endUserResult?.ref.username;
    } else {
      this.endUserResult = (this.phone() as Phone).owner && this.userDetailUiContext.getCurrentEnduser();
      this.username = this.endUserResult?.ref.username;
    }

    const siteSubscriptions = combineLatest([
      this.tileConfigContext.state$,
      this.siteContext.state$,
      this.currentClusterContext.state$,
    ]).subscribe(([tileConfig, siteResult, currentCluster]) => {
      this.siteId = siteResult.id;
      this.unityServerId = siteResult.unityServerId;
      this._currentCluster = currentCluster;
      this.isOneClickEnabled = this._hasOwner() && tileConfig.voicemail === ServiceProvisioningLevel.ONE_CLICK;
    });
    this._subscription.add(siteSubscriptions);

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((update) => {
      this.hideHelpText = false;
      if (update.new.dn?.extension !== update.old?.dn?.extension) {
        this.fieldChannels.voicemail.entitySource.next('');
      }
      if (!!this.dnRef() && !this.isSelectCleared) {
        this.voicemailRef = this.lineButtonSummary?.voicemail;
      }
      this.isSelectCleared = false;
      this.updateVmExtension = false;
      this._reValidateFields();
    });
    this._subscription.add(formUpdateSub);

    // assuming we won't be able to update other buttons while this popover is open...
    this._otherButtons = this.phone().buttons.filter((btn, idx) => idx !== this.buttonIndex());
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscription.unsubscribe();
  }

  addVoicemailOneClick(): void {
    this.isVoicemailAdding = true;
    const saveParams: VoicemailServiceParams = {
      endUserResult: this.userDetailUiContext.getCurrentEnduser(),
      endUsers: this.userDetailUiContext.getEndUsers(),
      unityServerId: this.unityServerId,
      siteId: Number(this.userDetailUiContext.getCurrentSite().id),
      dnRef: this.userDetailUiContext.getCurrentPrimaryExtension(),
      hasSnr: !!this.userDetailUiContext.hasSnr(),
      hasExtensionMobility: !!this.userDetailUiContext.hasExtensionMobility(),
    };
    this.voicemailService.provisionVoicemailWithDefaults(saveParams).subscribe((ref) => {
      this.lineButtonSummary = {
        ...this.lineButtonSummary,
        voicemail: ref,
      };
      this.phoneButtonsService.lineButtonSummary.next(this.lineButtonSummary);
      this.phoneButtonsService.isVoicemailDeleted.next(false);
      this.formData.voicemail = ref.alias;
      this._showVoicemailAddSuccessToast(ref);
      this._pushVoicemailRefToGlobal360(ref);
      this.isVoicemailAdding = false;
    });
  }

  private _initForm(): void {
    this.formConfig = {
      fields: {
        extensionSearch: {
          label: 'tkey;shared.device.phone.phone_buttons.line_button.line_extension.search.extension.label',
          dataAutomation: 'extension-search',
          validation: [
            {
              validator: (val: ZiroSelectOption) =>
                this.phoneButtonsService.validUniqueLineExtension(val?.value, this._otherButtons)
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.INVALID,
              message: 'tkey;shared.device.phone.phone_buttons.line_button.line_extension.search.unique.error',
            },
            {
              validator: (val: ZiroSelectOption) =>
                !val || this.formData.dialPlanGroupId !== null
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.WARNING,
              message: 'tkey;shared.device.phone.phone_buttons.line_button.line_extension.search.helptext.warning.text',
            },
          ],
          componentConfig: new ZiroSelectConfig({
            asyncOptionsFn: this.searchExtension.bind(this),
            placeholder: 'tkey;shared.device.phone.phone_buttons.line_button.line_extension.search.placeholder',
            triggerLoading: () => {
              return this.isPending();
            },
            clearWithInput: true,
            hideDropdownArrow: true,
            minSearchLength: 1,
          }),
          required: () => this.buttonIndex() === 0,
          disabled: () => this.isSingleButton() && this.phoneType !== PhoneType.DESKPHONE,
          disabledTooltip: 'tkey;shared.device.phone.phone_buttons.extension.disabled_tooltip',
        },
        voicemail: {
          label: 'tkey;shared.device.phone.phone_buttons.line_button.line_extension.search.voicemail_alias.label',
          dataAutomation: 'voicemail',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
            readonly: true,
          }),
          required: false,
          hidden: () => true,
        },
      },
      options: {
        columnClasses: {
          label: 'col-3 text-lg-end',
          input: 'col-9',
        },
      },
    };
  }

  private _updateSmtpAndHomeCluster(vmRef: VoicemailRef): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      this.isUpdatingSmtpOrHomeCluster = true;

      const requests: Observable<SmtpAddress | void | EndUserRef[] | void[]>[] = [];
      if (this._hasOwner()) {
        requests.push(
          this.voicemailService.setSmtpNotificationsDeviceDefaults(
            vmRef,
            this.username,
            this.unityServerId,
            this.siteId,
          ),
        );
        if (this.lineButtonSummary?.isPrimaryExtension) {
          requests.push(
            this.voicemailService.setHomeCluster(this.userDetailUiContext.getEndUsers(), this.endUserResult.ref.id),
          );
        }
      } else {
        requests.push(of(null));
      }
      forkJoin(requests)
        .pipe(first())
        .subscribe(() => {
          this.voicemailRef = vmRef;
          this.formData.voicemail = vmRef.alias;
          this.isUpdatingSmtpOrHomeCluster = false;
          subscriber.next(true);
          subscriber.complete();
        });
    });
  }

  private _showVoicemailAddSuccessToast(voicemailRef: VoicemailRef): void {
    this.toastService.push(
      ToastTypes.SUCCESS,
      this.ziroIcons.VOICEMAIL_SMALL,
      'tkey;shared.toast.save.success.title',
      'tkey;userdetail.voicemail.save.toast.success.text',
      { alias: voicemailRef.alias },
    );
  }

  openDetailedModal(): void {
    const modalUsername =
      this.phoneType === PhoneType.EXTENSION_MOBILITY
        ? this.userDetailUiContext.getCurrentEnduser().ref.username
        : (this.phone() as Phone).owner
          ? (this.phone() as Phone).owner.username
          : null;
    const options = {
      size: 'xxl',
      modalViewProperties: {
        title: 'tkey;dndidselection.modal.legend',
        phone: this.phone(),
        username: modalUsername,
        dnRef: this.dnRef(),
        translationPattern: this.entity.peripherals?.didTranslationPattern,
        isEndUserPrimary: this.entity.peripherals?.isPrimaryExtension,
        lineNum: modalUsername ? null : this.buttonIndex(),
        global360View: this.global360View(),
        peripherals: this.entity.peripherals,
        setIsSaveDisabled: true,
        siteId: this.siteId,
        userDetailUiContext: this.userDetailUiContext,
        lineButtonSummary: this.lineButtonSummary,
      },
      bodyClass: ZiroDirectoryNumberDetailedModalComponent,
    };
    this.ziroModalService
      .openDetailedModal(() => options.modalViewProperties, options)
      .subscribe((result: any) => {
        if (!result) {
          return;
        }

        // This should be emitted first after closing the modal. Or else, we do not get the extension updated.
        this.directoryNumberCreated.emit(result);

        const dnRef = this.dnRef();
        if (this.voicemailRef && !!dnRef && dnRef.extension !== result.dnRef.extension) {
          this.updateVmExtension = true;
          this._updateSmtpAndHomeCluster(result.vmRef).subscribe(() =>
            this._pushVoicemailRefToGlobal360(this.voicemailRef),
          );
        }

        if (result.translationPatterns) {
          this.phoneButtonsService.updateTpRef.next(result.translationPatterns);
        }

        /**If existing line button, update the line button summary **/
        if (this.lineButtonSummary) {
          const tpRef = result.translationPatterns
            ? result.translationPatterns
            : this.lineButtonSummary.didTranslationPattern;
          this.lineButtonSummary = {
            ...this.lineButtonSummary,
            didTranslationPattern: tpRef,
            isUccxAgent: !!result.endUser && !!result.endUser.ipccExtension?.id,
          };
          this.phoneButtonsService.lineButtonSummary.next(this.lineButtonSummary);
        }

        /** Update 360 view when modifying IPCC extension on a line button DN**/
        const global360 = cloneDeep(this.userDetailUiContext.getGlobal360View());
        if (global360) {
          const ipccIndex = global360.ipccExtensions.findIndex(
            (extension: DirectoryNumberRef) => extension.id === result.dnRef.id,
          );

          if (!!result.endUser && !!result.endUser.ipccExtension && !!result.endUser.ipccExtension?.id) {
            if (ipccIndex > -1) {
              global360.ipccExtensions[ipccIndex] = result.dnRef;
            } else {
              global360.ipccExtensions.push(result.dnRef);
              const username =
                this.phoneType === PhoneType.EXTENSION_MOBILITY
                  ? result.endUser.username
                  : (this.phone() as Phone).owner.username;
              if (this.entity.peripherals?.isPrimaryExtension) {
                this._uccxAgentResource.search(username).subscribe((data: UccxAgentResult[]) => {
                  global360.uccxAgents = data.map((agentResult: UccxAgentResult) => agentResult.ref);
                  this._emitLineFeatureSettings();
                });
              } else {
                setTimeout(() => {
                  this._uccxAgentResource.search(username).subscribe((data: UccxAgentResult[]) => {
                    global360.uccxAgents = data.map((agentResult: UccxAgentResult) => agentResult.ref);
                    this.global360ViewContext._stateSource.next(global360);
                    this._emitLineFeatureSettings();
                  });
                });
              }
            }
            this._emitLineFeatureSettings();
          } else if (
            !!result.endUser &&
            !!result.endUser.ipccExtension &&
            !result.endUser.ipccExtension.id &&
            ipccIndex > -1
          ) {
            global360.ipccExtensions.splice(ipccIndex, 1);
            this._emitLineFeatureSettings();
          }
          this._emitLineFeatureSettings();
          this.global360ViewContext._stateSource.next(global360);
        }
        this.modalData = result;
      });
  }

  openAddOrEditVmDetailedModal(voicemailRef?: VoicemailRef): void {
    this.isVoicemailAdding = true;

    (this._hasOwner()
      ? this.endUserResource.get(this.endUserResult.ref.id, this.endUserResult.ref.serverId)
      : of(null)
    ).subscribe((endUser: EndUser) => {
      const options = {
        size: 'lg',
        modalViewProperties: {
          title: voicemailRef
            ? 'tkey;shared.device.phone.phone_buttons.line_button.line_extension.voicemail.modal.edit.header_text'
            : 'tkey;shared.device.phone.phone_buttons.line_button.line_extension.voicemail.modal.add.header_text',
          phone: this.phone(),
          button: this.phone().buttons[this.buttonIndex()],
          username: this.username,
          dnRef: this.dnRef(),
          siteId: this.siteId,
          unityServerId: this.unityServerId,
          voicemailId: voicemailRef?.id,
          serverId: this.dnRef().serverId,
          lineButtonSummary: this.lineButtonSummary,
          setLoading: true,
          voiceMail: {
            alias: '',
            displayName: '',
            extension: '',
            firstName: '',
            lastName: '',
          } as Voicemail,
          voicemailAlternateExtension: [{ id: '', extension: '' }] as VoicemailAlternateExtension[],
          endUser: endUser,
          showDeleteButton: !!voicemailRef,
        },
        bodyClass: VoicemailDetailedModalComponent,
      };
      this.ziroModalService
        .openDetailedModal(() => options.modalViewProperties, options)
        .subscribe((result: any) => {
          if (!!result && !result.isCancelClicked) {
            if (!this.voicemailRef || (!!this.voicemailRef && !isEqual(this.voicemailRef, result))) {
              this._pushVoicemailRefToGlobal360(result);
            }
            this.voicemailRef = result;
            this.formData.voicemail = this.voicemailRef.alias;
            this.isVoicemailAdding = false;
            this._changeDetectorRef.detectChanges();
          } else if (!result) {
            this.phoneButtonsService.isVoicemailDeleted.subscribe((isVMDeleted) => {
              if (isVMDeleted) {
                this._removeVoicemailFromGlobal360(this.voicemailRef.id);
                if (this.isSingleButton()) {
                  this.isVoicemailAdding = false;
                  this._changeDetectorRef.detectChanges();
                }
              }
            });
          }
        });
    });
  }

  searchExtension(q: string): Observable<ZiroSelectOption[]> {
    const serverId = this._currentCluster.cucmServerId;
    return this.searchDirectoryNumberResource
      .get({
        q,
        'cucm-server-id': serverId,
      })
      .pipe(
        map((results: DirectoryNumberResult[]) => {
          return results.map((result: DirectoryNumberResult) => {
            return {
              label: this._getExtensionSearchLabel(result.ref),
              value: result.ref,
            };
          });
        }),
      );
  }

  submit(): Observable<any> {
    return this.parentSubmit$();
  }

  private _getFullExtensionDisplayLabel(directoryNumberRef: DirectoryNumberRef, translationPattern: string): string {
    let label = directoryNumberRef.extension;

    if (translationPattern && !directoryNumberRef.extension.includes(translationPattern)) {
      label += ` | ${this.telephoneNumberFilter.transform(translationPattern)}`;
    }
    if (directoryNumberRef.description) {
      label += ` - ${directoryNumberRef.description}`;
    }

    return label;
  }

  private _getExtensionSearchLabel = (directoryNumberRef: DirectoryNumberRef): string => {
    return directoryNumberRef.description
      ? `${directoryNumberRef.extension} - ${directoryNumberRef.description}`
      : directoryNumberRef.extension;
  };

  handleSelectClear(): void {
    this.hideHelpText = false;
    this.isSelectCleared = true;
    if (this._hasOwner()) {
      const global360 = cloneDeep(this.userDetailUiContext.getGlobal360View());
      global360.voicemails = global360.voicemails.filter(
        (vmRef: VoicemailRef) => vmRef.extension !== this.dnRef().extension,
      );
      this.global360ViewContext._stateSource.next(global360);
    }
    this.voicemailRef = null;
  }

  handleInputChange: ($event: string) => void = ($event: string) => {
    if (typeof $event === 'string') {
      this.hideHelpText = $event.length >= 2;
    }
  };

  handleOnBlur(): void {
    this.hideHelpText = false;
    // To prevent loss of input text if we backspace and leave the input
    if (!this.formData.extensionSearch) {
      this.entitySource.next({
        ...this.entity,
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    this._validateExtensionField();
    if (
      !!changes.lineButtonSummary &&
      !changes.lineButtonSummary.isFirstChange() &&
      changes.lineButtonSummary.previousValue
    ) {
      this.phoneButtonsService.lineButtonSummary.next(changes.lineButtonSummary.currentValue);
    }
  }

  isEndUserPrimary(): boolean {
    return this.global360View()?.primaryExtensions.some((ext) => ext.id === this.dnRef()?.id);
  }

  private _validateExtensionField(): void {
    this.fieldChannels['extensionSearch']?.validateSource.next();
  }

  toEntity = (formData: PhoneButtonsLineExtensionSearchFormData): PhoneButtonsLineExtensionSearchEntity => {
    return {
      peripherals:
        formData.extensionSearch && formData.extensionSearch.value.id === this.entity.peripherals?.id
          ? this.entity.peripherals
          : null,
      dn: formData.extensionSearch ? formData.extensionSearch.value : null,
    };
  };

  toFormData = (entity: PhoneButtonsLineExtensionSearchEntity): PhoneButtonsLineExtensionSearchFormData => {
    return {
      extensionSearch: entity.dn
        ? {
            label: this._getFullExtensionDisplayLabel(entity.dn, entity.peripherals?.didTranslationPattern?.pattern),
            value: entity.dn,
          }
        : null,
      voicemail: entity.peripherals?.voicemail ? entity.peripherals.voicemail.alias : '',
      dialPlanGroupId: entity.peripherals?.dialPlanGroupId,
    };
  };

  private _reValidateFields(): void {
    this.fieldComponents.forEach((fieldComponent) => {
      fieldComponent.showValidation = true;
    });
    this.fieldChannels['extensionSearch'].validateSource.next();
  }

  private _emitLineFeatureSettings(): void {
    this.reconsiderLineFeatureSettings.emit(true);
  }

  private _hasOwner(): boolean | EndUserRef {
    return this.phoneType === PhoneType.EXTENSION_MOBILITY || (this.phone() as Phone).owner;
  }

  private _pushVoicemailRefToGlobal360(voicemailRef: VoicemailRef): void {
    this.voicemailUpdated.emit(voicemailRef);
    if (this._hasOwner()) {
      const isVmOnPrimaryExtension =
        voicemailRef.extension === this.userDetailUiContext.getCurrentPrimaryExtension().extension;
      if (isVmOnPrimaryExtension) {
        const global360 = cloneDeep(this.userDetailUiContext.getGlobal360View());
        global360.voicemails.push(voicemailRef);
        this.global360ViewContext._stateSource.next(global360);
      }
    } else if (this.buttonIndex() === 0) {
      // If a public phone
      if (this.phoneType !== PhoneType.EXTENSION_MOBILITY) {
        this.injector.get(PhoneUiContext).setIsVoicemailPresentOnLineOne(true);
      }
    }
  }

  private _removeVoicemailFromGlobal360(voicemailId: string): void {
    if (typeof voicemailId === 'string') {
      const global360View = cloneDeep(this.userDetailUiContext.getGlobal360View());
      if (global360View) {
        global360View.voicemails = global360View?.voicemails.filter((voicemail) => voicemail.id !== voicemailId);
        this.global360ViewContext._stateSource.next(global360View);
      }
    }
  }
}
